import React, { useEffect, useRef, useState } from 'react';
import { FaAngleLeft, FaAngleRight } from "react-icons/fa6";
import { useAppSelector } from '../../../app/hooks';
import PortfolioComposite from '../../../api/models/portfolioComposite';
import { selectChildrenComposites } from './portfolio-detail-slice';
import PortfolioDetailCompositeSelectionCard from './portfolio-detail-composite-selection-card';

interface PortfolioDetailCompositeSelectionProps {
  portfolioComposite: PortfolioComposite
}

/**
 * A component to view and select a category from the portfolio.
 */
const PortfolioDetailCompositeSelection: React.FC<PortfolioDetailCompositeSelectionProps> = (props: PortfolioDetailCompositeSelectionProps) => {

  const childrenComposites = useAppSelector(selectChildrenComposites);

  const [scrollOnLeftMargin, setScrollOnLeftMargin] = useState(true);
  const [scrollOnRightMargin, setScrollOnRightMargin] = useState(true);
  function scrollHandler(e: React.UIEvent<HTMLDivElement, UIEvent>): void {
    // console.log(`e.currentTarget.scrollLeft: ${e.currentTarget.scrollLeft} / e.currentTarget.scrollWidth: ${e.currentTarget.scrollWidth}`);
    // console.log(`Math.abs(e.currentTarget.scrollLeft + e.currentTarget.clientWidth - e.currentTarget.scrollWidth) < 3: ${Math.abs(e.currentTarget.scrollLeft + e.currentTarget.clientWidth - e.currentTarget.scrollWidth) < 3}`);
    setScrollOnLeftMargin(e.currentTarget.scrollLeft === 0);
    setScrollOnRightMargin(Math.abs(e.currentTarget.scrollLeft + e.currentTarget.clientWidth - e.currentTarget.scrollWidth) < 3);
  }

  const maskTransparentBothSides = '[mask-image:linear-gradient(to_right,transparent_0,black_70px,black_calc(100%-70px),transparent_100%)]';
  const maskTransparentRightSide = '[mask-image:linear-gradient(to_right,black_0,black_70px,black_calc(100%-70px),transparent_100%)]';
  const maskTransparentLeftSide = '[mask-image:linear-gradient(to_right,transparent_0,black_70px,black_calc(100%-70px),black_100%)]';

  const refDivScrollable = useRef<HTMLDivElement>(null);
  const [scrollableIndex, setScrollableIndex] = useState(setInterval(() => { }));

  useEffect(() => {
    // console.log(`clientWidth: ${refDivScrollable.current?.clientWidth} / scrollWidth: ${refDivScrollable.current?.scrollWidth}`)
    setScrollOnRightMargin(Math.abs((refDivScrollable.current?.clientWidth ?? 0) - (refDivScrollable.current?.scrollWidth ?? 0)) < 3);
  }, [childrenComposites])

  function arrowScrollLeftHandler(e: React.MouseEvent<HTMLDivElement, MouseEvent>): void {
    refDivScrollable.current?.scrollBy({ left: -50 });
  }

  function arrowScrollRightHandler(e: React.MouseEvent<HTMLDivElement, MouseEvent>): void {
    refDivScrollable.current?.scrollBy({ left: 50 });
  }

  function arrowScrollLeftEnterHandler(e: React.MouseEvent<HTMLDivElement, MouseEvent>): void {
    !isMobile() && setScrollableIndex(setInterval(() => refDivScrollable.current?.scrollBy({ left: -3 }), 10));
  }

  function arrowScrollLeftLeaveHandler(e: React.MouseEvent<HTMLDivElement, MouseEvent>): void {
    !isMobile() && clearInterval(scrollableIndex);
  }

  function arrowScrollRightEnterHandler(e: React.MouseEvent<HTMLDivElement, MouseEvent>): void {
    !isMobile() && setScrollableIndex(setInterval(() => refDivScrollable.current?.scrollBy({ left: 3 }), 10));
  }

  function arrowScrollRightLeaveHandler(e: React.MouseEvent<HTMLDivElement, MouseEvent>): void {
    !isMobile() && clearInterval(scrollableIndex);
  }

  function isMobile() {
    return window.outerWidth < 1024;
  }

  return (
    <div className='select-none flex flex-col w-full h-full items-center justify-start overflow-hidden'>
      <div className='lg:hidden flex flex-row w-full h-fit py-3 items-center'>
        <div className='grow flex flex-col [mask-image:linear-gradient(to_right,transparent_0,black_150px,black_100%)]'>
          <div className='h-1/2 mx-3 border-b border-blue-300/40'></div>
          <div className='h-1/2 mx-3'></div>
        </div>
        <label className='text-base'>Filtrar por categoria</label>
        <div className='grow flex flex-col [mask-image:linear-gradient(to_right,black_0,black_calc(100%-150px),transparent_100%)]'>
          <div className='h-1/2 mx-3 border-b border-blue-300/40'></div>
          <div className='h-1/2 mx-3'></div>
        </div>
      </div>
      <div className='flex flex-col w-full h-full px-9 lg:pb-9 items-center justify-start overflow-hidden'>
        <div className='relative h-full w-full rounded-lg overflow-hidden lg:bg-black/10 lg:border-2 lg:border-blue-300/40 lg:shadow-2xl lg:shadow-slate-900/80'>
          <div onMouseEnter={arrowScrollLeftEnterHandler} onMouseLeave={arrowScrollLeftLeaveHandler} onClick={arrowScrollLeftHandler} className={`z-10 w-7 px-1 cursor-pointer ${scrollOnLeftMargin ? '!hidden' : 'flex'} flex-row absolute left-0 w-10 pb-5 h-full items-center`}>
            <FaAngleLeft size={100} className='text-blue-200/80' />
          </div>
          <div onMouseEnter={arrowScrollRightEnterHandler} onMouseLeave={arrowScrollRightLeaveHandler} onClick={arrowScrollRightHandler} className={`z-10 w-7 px-1 cursor-pointer ${scrollOnRightMargin ? '!hidden' : 'flex'} lg:flex flex-row-reverse absolute right-0 w-10 pb-5 h-full items-center`}>
            <FaAngleRight size={100} className='text-blue-200/80' />
          </div>
          <label className='max-lg:hidden absolute top-2 left-0 right-0 text-center text-xl lg:text-2xl'>Filtrar por categoria</label>
          <div ref={refDivScrollable} onScroll={scrollHandler} className={`absolute box-content top-0 left-0 right-0 h-full pb-10 overflow-x-auto ${scrollOnLeftMargin ? maskTransparentRightSide : scrollOnRightMargin ? maskTransparentLeftSide : maskTransparentBothSides}`}>
            <div className='flex flex-row w-full h-full items-center justify-start lg:px-10 space-x-5 lg:[&>*]:h-36 [&>*]:shrink-0'>
              {
                childrenComposites && childrenComposites.length > 0 &&
                childrenComposites.map(childComposite =>
                  <PortfolioDetailCompositeSelectionCard
                    key={childComposite.id}
                    portfolioComposite={childComposite} />)
              }
              {
                childrenComposites?.length === 0 &&
                <label className='w-full text-center opacity-50'>
                  - nenhuma subcategoria -
                </label>
              }
            </div>
          </div>
        </div>
      </div>
    </div >
  )
}

export default PortfolioDetailCompositeSelection;